import React, { useEffect, useRef, useState } from 'react';
import styles from './inputwithDropdown.module.css';

const getInputLabelSuffix = (isOptional, isRequired) => {
    if (isOptional) {
        return ' (Optional)';
    }
    if (isRequired) {
        return '*';
    }
    return null;
};

const InputWithDropdown = ({
    options = [],
    placeholder = 'Type or select',
    onChange,
    label,
    isOptional,
    isRequired,
    labelKey = 'label',
    value = '',
    error,
    errorMessage,
    isDisabled = false,
}) => {
    const [inputValue, setInputValue] = useState(value);
    const [selectedOpt, setSelectedOpt] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    const handleInputChange = (e) => {
        const value = e.target.value;
        setInputValue(value);
        setShowDropdown(true);
        // Check if input value matches any option
        const matchedOption = (options || []).find((option) => {
            const optionValue = typeof option === 'string' ? option : option[labelKey];
            return optionValue?.toLowerCase() === value.toLowerCase();
        });

        if (matchedOption) {
            setSelectedOpt(matchedOption);
            handleOptionSelect(matchedOption); // Automatically select the matched option
        } else {
            setSelectedOpt(null);
        }
    };

    const handleOptionSelect = (option) => {
        const value = typeof option === 'string' ? option : option[labelKey];
        setInputValue(value);
        setShowDropdown(false);
        setSelectedOpt(option);
    };

    const prevInputValue = useRef(inputValue);
    const prevSelectedOpt = useRef(selectedOpt);

    useEffect(() => {
        if (onChange && (prevInputValue.current !== inputValue || prevSelectedOpt.current !== selectedOpt)) {
            onChange(inputValue, selectedOpt);
            prevInputValue.current = inputValue;
            prevSelectedOpt.current = selectedOpt;
        }
    }, [inputValue, selectedOpt, onChange]);

    const filteredOptions = (options || [])?.filter((option) => {
        const optionValue = typeof option === 'object' && option !== null ? option[labelKey] : '';

        return optionValue && optionValue.toLowerCase().includes((inputValue ?? '').toString().toLowerCase());
    });

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const dropdownStyles = {
        listStyleType: 'none',
        margin: 0,
        padding: 0,
        border: '1px solid #ccc',
        borderRadius: '4px',
        position: 'absolute',
        width: '100%',
        maxHeight: '150px',
        overflowY: 'auto',
        backgroundColor: '#fff',
        zIndex: 1,
    };

    return (
        <>
            <div ref={dropdownRef} style={{ position: 'relative' }}>
                {label ? (
                    <label className='form-label text-neutral-850 font-b3 mb-1'>
                        {label}
                        {getInputLabelSuffix(isOptional, isRequired)}
                    </label>
                ) : null}
                <input
                    type='text'
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={() => setShowDropdown(true)}
                    placeholder={placeholder}
                    className={'form-control form-control-sm border-none'}
                    disabled={isDisabled}
                />
                {showDropdown && filteredOptions.length > 0 && (
                    <ul style={dropdownStyles}>
                        {filteredOptions.map((option) => {
                            const optionValue = typeof option === 'string' ? option : option[labelKey];
                            return (
                                <li
                                    className={`font-b2 ${styles.listBox}`}
                                    key={optionValue}
                                    onClick={() => handleOptionSelect(option)}>
                                    {optionValue}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
            {error && <p className='text-error-700 font-b3 font-regular mt-2'>{errorMessage}</p>}
        </>
    );
};

export default InputWithDropdown;
